<template>
  <v-dialog persistent width="960" v-model="dialog">
    <v-card rounded="0">
      <v-toolbar color="secondary" class="white--text" dense>
        <v-toolbar-title class="font-weight-bold text-subtitle-1">
          <v-icon left class="white--text">mdi-vector-polygon</v-icon>
          Edit Polygon — Fix Self-Intersection
        </v-toolbar-title>
        <v-spacer></v-spacer>
        <v-btn icon class="white--text" @click="cancel">
          <v-icon>mdi-close</v-icon>
        </v-btn>
      </v-toolbar>

      <!-- Status bar -->
      <div class="polygon-status-bar px-4 py-2 d-flex align-center flex-wrap" style="gap:8px">
        <span class="caption text--secondary">
          <strong>{{ row.farmName || '—' }}</strong>
          <span v-if="row.country"> · {{ row.country }}</span>
          <span class="ml-2 grey--text">Row #{{ row.rowIndex }}</span>
        </span>
        <v-spacer></v-spacer>

        <!-- Delete mode toggle -->
        <v-btn
          v-if="mapReady"
          x-small
          :color="deleteMode ? 'error' : 'grey lighten-2'"
          :dark="deleteMode"
          elevation="0"
          @click="toggleDeleteMode"
        >
          <v-icon left x-small>mdi-vector-point-minus</v-icon>
          {{ deleteMode ? 'Exit Delete Mode' : 'Delete Vertices' }}
        </v-btn>

        <v-chip v-if="hasSelfIntersect" small color="error" dark>
          <v-icon left x-small>mdi-alert-circle</v-icon>
          Self-intersecting — drag vertices to fix
        </v-chip>
        <v-chip v-else-if="mapReady" small color="success" dark>
          <v-icon left x-small>mdi-check-circle</v-icon>
          Valid polygon
        </v-chip>
        <span v-if="vertexCount" class="caption text--secondary">{{ vertexCount }} vertices</span>
      </div>

      <!-- Map -->
      <div
        ref="mapEl"
        class="polygon-map"
        :style="{ height: '480px', width: '100%', background: '#e8e8e8' }"
      >
        <div v-if="!mapReady" class="d-flex align-center justify-center" style="height:100%">
          <v-progress-circular indeterminate color="secondary" size="48" />
        </div>
      </div>

      <!-- Instructions -->
      <div class="px-4 py-2 caption text--secondary" style="background:#fafafa;border-top:1px solid #eee">
        <template v-if="!deleteMode">
          <v-icon x-small>mdi-cursor-move</v-icon>
          Drag white vertex handles to reshape the polygon.
          <v-icon x-small class="ml-2">mdi-plus-circle-outline</v-icon>
          Click midpoint dots to add a vertex.
          <v-icon x-small class="ml-2">mdi-minus-circle-outline</v-icon>
          Right-click a vertex to delete it.
          <v-icon x-small class="ml-2">mdi-vector-point-minus</v-icon>
          Or use <strong>Delete Vertices</strong> mode to click-remove vertices.
        </template>
        <template v-else>
          <v-icon x-small color="error">mdi-vector-point-minus</v-icon>
          <span class="error--text font-weight-medium">Delete mode active</span> — click a red marker to remove that vertex.
          Minimum 3 vertices required. Click <strong>Exit Delete Mode</strong> to resume dragging.
        </template>
      </div>

      <v-card-actions class="px-4 py-3" style="border-top:1px solid #e0e0e0">
        <v-spacer></v-spacer>
        <v-btn text @click="cancel">Cancel</v-btn>
        <v-btn
          color="secondary"
          elevation="0"
          :disabled="hasSelfIntersect || !mapReady"
          @click="save"
        >
          <v-icon left small>mdi-check</v-icon>
          Save Polygon
        </v-btn>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>

<script>
import { getGoogleMapsLoader } from '@/mixins/GoogleMapLoaderSingleton';
import { Point, Polygon } from '@flatten-js/core';

export default {
  name: 'FarmPolygonEditDialog',

  props: {
    value: { type: Boolean, required: true },
    row: { type: Object, default: () => ({}) },
    // [[lng, lat], ...] — GeoJSON coordinate order
    geofence: { type: Array, default: () => [] },
  },

  data() {
    return {
      google: null,
      map: null,
      polygon: null,
      mapReady: false,
      hasSelfIntersect: false,
      vertexCount: 0,
      deleteMode: false,
      deleteMarkers: [],
    };
  },

  computed: {
    dialog: {
      get() { return this.value; },
      set(v) { this.$emit('input', v); },
    },
  },

  watch: {
    value(open) {
      if (open) {
        this.mapReady = false;
        this.hasSelfIntersect = false;
        this.deleteMode = false;
        this.$nextTick(() => this.initMap());
      } else {
        this.destroyMap();
      }
    },
  },

  methods: {
    async initMap() {
      try {
        if (!this.google) {
          this.google = await getGoogleMapsLoader().load();
        }

        // Convert [[lng, lat], ...] → { lat, lng } for Google Maps
        const coords = (this.geofence || []).map(([lng, lat]) => ({ lat: Number(lat), lng: Number(lng) }));

        const center = coords.length
          ? {
              lat: coords.reduce((s, c) => s + c.lat, 0) / coords.length,
              lng: coords.reduce((s, c) => s + c.lng, 0) / coords.length,
            }
          : { lat: 0, lng: 0 };

        this.map = new this.google.maps.Map(this.$refs.mapEl, {
          zoom: 15,
          center,
          mapTypeId: 'satellite',
          fullscreenControl: false,
          streetViewControl: false,
        });

        if (coords.length >= 2) {
          const bounds = new this.google.maps.LatLngBounds();
          coords.forEach(c => bounds.extend(c));
          this.map.fitBounds(bounds, 60);
        }

        this.polygon = new this.google.maps.Polygon({
          paths: coords,
          strokeColor: '#FF4444',
          strokeOpacity: 0.9,
          strokeWeight: 2,
          fillColor: '#FF8888',
          fillOpacity: 0.3,
          editable: true,
          draggable: false,
        });
        this.polygon.setMap(this.map);

        const path = this.polygon.getPath();
        this.google.maps.event.addListener(path, 'set_at', () => this.onPathChanged());
        this.google.maps.event.addListener(path, 'insert_at', () => this.onPathChanged());
        this.google.maps.event.addListener(path, 'remove_at', () => this.onPathChanged());

        this.mapReady = true;
        this.onPathChanged();
      } catch (err) {
        console.error('[FarmPolygonEditDialog] map init error', err);
      }
    },

    onPathChanged() {
      if (!this.polygon) return;
      const path = this.polygon.getPath();
      this.vertexCount = path.getLength();
      this.checkSelfIntersect();
    },

    checkSelfIntersect() {
      if (!this.polygon) return;
      const path = this.polygon.getPath();
      const n = path.getLength();
      if (n < 3) {
        this.hasSelfIntersect = false;
        return;
      }
      const points = [];
      for (let i = 0; i < n; i++) {
        const ll = path.getAt(i);
        points.push(new Point(ll.lat(), ll.lng()));
      }
      const poly = new Polygon(points);
      this.hasSelfIntersect = !poly.isValid();
    },

    toggleDeleteMode() {
      this.deleteMode = !this.deleteMode;
      if (!this.polygon) return;
      this.polygon.setEditable(!this.deleteMode);
      if (this.deleteMode) {
        this.addDeleteMarkers();
      } else {
        this.removeDeleteMarkers();
      }
    },

    addDeleteMarkers() {
      this.removeDeleteMarkers();
      if (!this.polygon) return;
      const path = this.polygon.getPath();
      for (let i = 0; i < path.getLength(); i++) {
        const latLng = path.getAt(i);
        const marker = new this.google.maps.Marker({
          position: latLng,
          map: this.map,
          icon: {
            path: this.google.maps.SymbolPath.CIRCLE,
            scale: 9,
            fillColor: '#FF4444',
            fillOpacity: 1,
            strokeColor: '#FFFFFF',
            strokeWeight: 2,
          },
          cursor: 'pointer',
          title: 'Click to delete vertex',
          zIndex: 10,
        });
        const capturedIndex = i;
        marker.addListener('click', () => this.deleteVertex(capturedIndex));
        this.deleteMarkers.push(marker);
      }
    },

    removeDeleteMarkers() {
      this.deleteMarkers.forEach(m => {
        this.google.maps.event.clearInstanceListeners(m);
        m.setMap(null);
      });
      this.deleteMarkers = [];
    },

    deleteVertex(index) {
      if (!this.polygon) return;
      const path = this.polygon.getPath();
      if (path.getLength() <= 3) return; // keep minimum 3
      path.removeAt(index);
      this.onPathChanged();
      // Re-render markers because indices have shifted
      if (this.deleteMode) this.addDeleteMarkers();
    },

    save() {
      if (this.hasSelfIntersect || !this.polygon) return;
      const path = this.polygon.getPath();
      const geofence = [];
      for (let i = 0; i < path.getLength(); i++) {
        const ll = path.getAt(i);
        geofence.push([ll.lng(), ll.lat()]); // [lng, lat] GeoJSON order
      }
      this.$emit('saved', { rowIndex: this.row.rowIndex, geofence });
      this.$emit('input', false);
    },

    cancel() {
      this.$emit('input', false);
    },

    destroyMap() {
      this.removeDeleteMarkers();
      this.deleteMode = false;
      if (this.polygon) {
        this.google.maps.event.clearInstanceListeners(this.polygon.getPath());
        this.polygon.setMap(null);
        this.polygon = null;
      }
      this.map = null;
      this.mapReady = false;
    },
  },
};
</script>

<style scoped>
.polygon-status-bar {
  background: #f5f5f5;
  border-bottom: 1px solid #e0e0e0;
  min-height: 40px;
}
</style>
